<template>
  <div class="sala-main-container">
    <!-- Ambient Alert Banner: Unclosed Orders from Previous Days (Von Restorff Saliency) -->
    <transition name="fade-page">
      <div v-if="ordiniPrecedenti.length > 0" class="ordini-precedenti-banner sci-card">
        <div class="banner-icon-col">
          <div class="banner-pulse-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
        </div>
        <div class="banner-info-col">
          <h2 class="banner-title">
            Attenzione: Rilevati {{ ordiniPrecedenti.length }} ordini aperti dai giorni precedenti
          </h2>
          <p class="banner-subtitle">
            Questi tavoli non sono stati chiusi nei turni precedenti e possono bloccare l'ordinazione odierna.
          </p>
        </div>
        <div class="banner-action-col">
          <button 
            type="button" 
            class="sci-btn sci-btn-warning btn-touch"
            @click="apriModalOrdiniPrecedenti"
          >
            <i class="fas fa-history me-1"></i>
            Gestisci e Chiudi Ordini ({{ ordiniPrecedenti.length }})
          </button>
        </div>
      </div>
    </transition>

    <!-- Top Action Bar -->
    <div class="sala-header-bar">
      <div class="header-info">
        <h1 class="header-title">{{ $t('sala') }} — Gestione Ambienti</h1>
        <p class="header-subtitle">Seleziona una sala per monitorare i tavoli in tempo reale o configurane una nuova.</p>
      </div>

      <div class="header-actions">
        <button 
          v-if="ordiniPrecedenti.length > 0"
          type="button" 
          class="sci-btn sci-btn-secondary me-2"
          @click="apriModalOrdiniPrecedenti"
        >
          <i class="fas fa-clock text-warning me-1"></i>
          <span>Ordini da Ieri ({{ ordiniPrecedenti.length }})</span>
        </button>

        <button 
          type="button" 
          class="sci-btn" 
          :class="page === 'crea' ? 'sci-btn-secondary' : 'sci-btn-primary'"
          @click="toggleCrea"
        >
          <i :class="page === 'crea' ? 'fas fa-times' : 'fas fa-plus'"></i>
          <span>{{ page === 'crea' ? 'Chiudi' : $t('crea_sala') }}</span>
        </button>
      </div>
    </div>

    <!-- Collapsible Creation Panel -->
    <transition name="expand">
      <div v-if="page === 'crea'" class="crea-panel sci-card mb-4">
        <div class="panel-header">
          <h3 class="panel-title">
            <i class="fas fa-plus-circle text-primary me-2"></i>
            {{ $t('crea_sala') }}
          </h3>
          <span class="panel-desc">Definisci il nome dell'ambiente e la capienza in tavoli</span>
        </div>

        <form @submit.prevent="getSala" class="crea-form">
          <div class="form-row">
            <div class="form-col">
              <label class="form-label">{{ $t('inserisci_nome_sala') }}</label>
              <div class="input-with-icon">
                <i class="fas fa-door-open input-icon"></i>
                <input 
                  type="text" 
                  class="sci-input" 
                  :placeholder="$t('inserisci_nome_sala')" 
                  v-model="nome"
                  required
                />
              </div>
            </div>

            <div class="form-col">
              <label class="form-label">{{ $t('inserisci_numero_tavoli') }}</label>
              <div class="input-with-icon">
                <i class="fas fa-chair input-icon"></i>
                <input 
                  type="number" 
                  min="1"
                  max="200"
                  class="sci-input" 
                  :placeholder="$t('inserisci_numero_tavoli')" 
                  v-model="numero"
                  required
                />
              </div>
            </div>
          </div>

          <div class="panel-actions">
            <button type="button" class="sci-btn sci-btn-secondary" @click="page = ''">
              Annulla
            </button>
            <button type="submit" class="sci-btn sci-btn-emerald">
              <i class="fas fa-check me-1"></i>
              Salva e Crea Sala
            </button>
          </div>
        </form>
      </div>
    </transition>

    <!-- Empty State -->
    <div v-if="sale.length === 0" class="empty-rooms sci-card">
      <i class="fas fa-layer-group empty-icon"></i>
      <h3>Nessuna sala configurata</h3>
      <p>Crea la tua prima sala con il pulsante sopra per iniziare a gestire le comande e i tavoli.</p>
      <button type="button" class="sci-btn sci-btn-primary" @click="page = 'crea'">
        <i class="fas fa-plus me-1"></i> Crea la prima Sala
      </button>
    </div>

    <!-- Grid of Rooms -->
    <div v-else class="rooms-grid">
      <div 
        v-for="sala in sale" 
        :key="sala.nome"
        class="room-card sci-card"
        :class="{ 'room-has-precedenti': getOpenOrdersCount(sala.nome) > 0 }"
      >
        <div class="room-card-header">
          <div class="room-icon-badge">
            <i class="fas fa-utensils"></i>
          </div>
          <button 
            type="button" 
            class="room-delete-btn" 
            @click="deleteSala(sala.nome)" 
            :title="$t('elimina_sala')"
            aria-label="Elimina Sala"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>

        <div class="room-card-body">
          <h2 class="room-name">{{ sala.nome }}</h2>
          <div class="room-meta">
            <span class="sci-badge sci-badge-primary">
              <i class="fas fa-chair"></i>
              {{ sala.numero }} Tavoli
            </span>
            <span class="sci-badge sci-badge-success">
              <i class="fas fa-circle" style="font-size: 8px;"></i>
              Attiva
            </span>
            <span 
              v-if="getOpenOrdersCount(sala.nome) > 0" 
              class="sci-badge sci-badge-warning"
              :title="getOpenOrdersCount(sala.nome) + ' ordini rimasti aperti dai giorni precedenti'"
            >
              <i class="fas fa-exclamation-circle"></i>
              {{ getOpenOrdersCount(sala.nome) }} da ieri
            </span>
          </div>
        </div>

        <div class="room-card-footer">
          <button 
            type="button" 
            class="sci-btn sci-btn-primary enter-room-btn" 
            @click="goToSala(sala.nome, sala.numero)"
          >
            <span>Apri Mappa Sala</span>
            <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Gestione Ordini Rimasti Aperti dai Giorni Precedenti -->
    <div v-if="showModalOrdiniPrecedenti" class="sci-modal-overlay" tabindex="-1" role="dialog" aria-modal="true">
      <div class="sci-modal-card">
        <div class="sci-modal-header">
          <div class="d-flex align-items-center gap-3">
            <div class="modal-icon-badge bg-warning-subtle text-warning">
              <i class="fas fa-history fa-lg"></i>
            </div>
            <div>
              <h3 class="mb-0 fw-bold">Ordini Aperti dai Giorni Precedenti</h3>
              <div class="text-muted small">Visualizza, chiudi conto o elimina i piatti/tavoli lasciati aperti</div>
            </div>
          </div>
          <button type="button" class="btn-close-modal" @click="showModalOrdiniPrecedenti = false" aria-label="Chiudi">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="sci-modal-body">
          <div v-if="ordiniPrecedenti.length === 0" class="text-center py-5">
            <i class="fas fa-check-circle text-success fa-3x mb-3"></i>
            <h4>Nessun ordine aperto pendente!</h4>
            <p class="text-muted">Tutti gli ordini dei giorni precedenti risultano regolarmente chiusi o archiviati.</p>
          </div>

          <div v-else class="ordini-precedenti-list">
            <div 
              v-for="ordine in ordiniPrecedenti" 
              :key="ordine.idO || ordine._id" 
              class="ordine-precedente-item sci-card"
            >
              <div class="item-header">
                <div class="item-title-box">
                  <span class="badge-tavolo">Tavolo {{ cleanTableNum(ordine.tavolo || ordine.numero) }}</span>
                  <span class="badge-sala"><i class="fas fa-map-marker-alt me-1"></i>{{ ordine.sala || ordine.codicesala }}</span>
                  <span class="badge-date"><i class="fas fa-calendar-alt me-1"></i>{{ formatOrderTime(ordine.tm) }}</span>
                </div>
                <div class="item-meta-right">
                  <span class="badge-coperti" v-if="ordine.coperti"><i class="fas fa-user-friends me-1"></i>{{ ordine.coperti }} coperti</span>
                  <span class="badge-cameriere" v-if="ordine.cameriere"><i class="fas fa-user me-1"></i>{{ ordine.cameriere }}</span>
                </div>
              </div>

              <!-- Dishes List -->
              <div class="item-dishes-table">
                <div 
                  v-for="(p, pIdx) in getOrdinazione(ordine)" 
                  :key="pIdx" 
                  class="dish-row"
                >
                  <div class="dish-qty-name">
                    <span class="dish-qty">{{ p.quantita || 1 }}x</span>
                    <span class="dish-name">{{ p.piatto || p.nome || 'Piatto' }}</span>
                    <span class="dish-additions" v-if="p.aggiunta && p.aggiunta.length">
                      (+ {{ formatAdditions(p.aggiunta) }})
                    </span>
                    <span class="dish-subtractions" v-if="p.senza && p.senza.length">
                      (- {{ formatSubtractions(p.senza) }})
                    </span>
                    <span class="dish-note" v-if="p.nota">
                      <i class="fas fa-info-circle me-1"></i>{{ p.nota }}
                    </span>
                  </div>
                  <div class="dish-price-actions">
                    <span class="dish-price">€ {{ formatPrice(p) }}</span>
                    <button 
                      type="button" 
                      class="btn-delete-dish" 
                      @click="eliminaPiattoPrecedente(ordine, p)"
                      title="Elimina Piatto dall'Ordine"
                      aria-label="Elimina Piatto"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Order Summary & Actions -->
              <div class="item-footer">
                <div class="item-totale">
                  <span class="label-totale">Totale:</span>
                  <span class="valore-totale">€ {{ calcolaTotaleOrdine(ordine).toFixed(2) }}</span>
                </div>

                <div class="item-actions">
                  <button 
                    type="button" 
                    class="sci-btn sci-btn-secondary btn-sm"
                    @click="goToSalaTavolo(ordine.sala || ordine.codicesala)"
                  >
                    <i class="fas fa-external-link-alt me-1"></i>
                    Vai in Sala
                  </button>

                  <button 
                    type="button" 
                    class="sci-btn sci-btn-danger btn-sm"
                    @click="eliminaTavoloPrecedente(ordine)"
                  >
                    <i class="fas fa-ban me-1"></i>
                    Elimina Tavolo
                  </button>

                  <button 
                    type="button" 
                    class="sci-btn sci-btn-emerald btn-sm"
                    @click="apriChiusuraRapida(ordine)"
                  >
                    <i class="fas fa-check me-1"></i>
                    Chiudi Tavolo / Conto
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Close Payment Modal for Precedenti -->
    <div v-if="ordineInChiusura" class="sci-modal-overlay" style="z-index: 1200;" tabindex="-1">
      <div class="sci-modal-card" style="max-width: 520px;">
        <div class="sci-modal-header">
          <h4 class="mb-0 fw-bold">Chiudi Tavolo {{ cleanTableNum(ordineInChiusura.tavolo || ordineInChiusura.numero) }}</h4>
          <button type="button" class="btn-close-modal" @click="ordineInChiusura = null">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="sci-modal-body">
          <p class="text-muted mb-3">
            Conferma la chiusura del tavolo per l'ordine lasciato aperto da 
            <strong>{{ formatOrderTime(ordineInChiusura.tm) }}</strong>.
          </p>

          <div class="mb-3">
            <label class="form-label font-weight-bold">Metodo di Pagamento:</label>
            <div class="payment-methods-grid">
              <label 
                v-for="metodo in ['contanti', 'carta', 'digitale', 'senza_incasso']" 
                :key="metodo"
                class="payment-method-chip"
                :class="{ active: metodoPagamentoChiusura === metodo }"
              >
                <input type="radio" v-model="metodoPagamentoChiusura" :value="metodo" class="d-none" />
                <span class="text-capitalize">{{ metodo.replace('_', ' ') }}</span>
              </label>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Sconto (€):</label>
            <input type="number" min="0" step="0.5" class="sci-input" v-model.number="scontoChiusura" />
          </div>

          <div class="p-3 bg-light rounded mb-3 d-flex justify-content-between align-items-center">
            <span class="fw-bold">Totale Finale:</span>
            <span class="fs-4 fw-bold text-primary">
              € {{ Math.max(0, calcolaTotaleOrdine(ordineInChiusura) - (scontoChiusura || 0)).toFixed(2) }}
            </span>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="sci-btn sci-btn-secondary" @click="ordineInChiusura = null">Annulla</button>
            <button type="button" class="sci-btn sci-btn-emerald" @click="confermaChiusuraOrdine">
              <i class="fas fa-check me-1"></i> Conferma Chiusura
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { store } from '../store/store.js'

export default {
  name: 'Sala',
  props: {
    token: String,
  },
  data: function () {
    return {
      page: '',
      nome: '',
      numero: '',
      sale: [],
      numeroSale: [],
      nomeSale: [],
      ordiniPrecedenti: [],
      showModalOrdiniPrecedenti: false,
      ordineInChiusura: null,
      metodoPagamentoChiusura: 'contanti',
      scontoChiusura: 0,
      store
    }
  },
  methods: {
    toggleCrea() {
      this.page = this.page === 'crea' ? '' : 'crea';
    },
    showOptions(e) {
      this.page = e.target.id === this.page ? '' : e.target.id;
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    cleanTableNum(raw) {
      if (!raw) return '';
      let s = String(raw);
      return s.startsWith('t') ? s.slice(1) : s;
    },
    formatOrderTime(tm) {
      if (!tm) return 'Data non disp.';
      const m = moment.unix(tm);
      const startOfToday = moment().startOf('day');
      const startOfYesterday = moment().subtract(1, 'day').startOf('day');

      if (m.isBetween(startOfYesterday, startOfToday)) {
        return `Ieri, ore ${m.format('HH:mm')}`;
      } else if (m.isBefore(startOfYesterday)) {
        return `${m.format('DD/MM/YYYY')} (ore ${m.format('HH:mm')})`;
      }
      return m.format('DD/MM/YYYY HH:mm');
    },
    getOpenOrdersCount(nomeSala) {
      if (!this.ordiniPrecedenti || !this.ordiniPrecedenti.length) return 0;
      return this.ordiniPrecedenti.filter(o => {
        const oSala = (o.sala || o.codicesala || '').trim().toLowerCase();
        return oSala === (nomeSala || '').trim().toLowerCase();
      }).length;
    },
    getOrdinazione(ordine) {
      if (ordine.ordinazione && Array.isArray(ordine.ordinazione)) return ordine.ordinazione;
      if (ordine.ordineDoc && ordine.ordineDoc.ordinazione && Array.isArray(ordine.ordineDoc.ordinazione)) {
        return ordine.ordineDoc.ordinazione;
      }
      if (ordine.ord && Array.isArray(ordine.ord)) return ordine.ord;
      return [];
    },
    formatPrice(dish) {
      const p = Number(dish.prezzo || 0);
      const a = Number(dish.costoAggiunta || 0);
      const q = Number(dish.quantita || 1);
      return ((p + a) * q).toFixed(2);
    },
    formatAdditions(additions) {
      if (!additions || !additions.length) return '';
      return additions.map(a => typeof a === 'object' ? (a.descrizione || a.nome || a) : a).join(', ');
    },
    formatSubtractions(senza) {
      if (!senza || !senza.length) return '';
      return senza.map(s => typeof s === 'object' ? (s.descrizione || s.nome || s) : s).join(', ');
    },
    calcolaTotaleOrdine(ordine) {
      const dishes = this.getOrdinazione(ordine);
      let tot = 0;
      dishes.forEach(d => {
        const p = Number(d.prezzo || 0);
        const a = Number(d.costoAggiunta || 0);
        const q = Number(d.quantita || 1);
        tot += (p + a) * q;
      });
      return tot;
    },
    apriModalOrdiniPrecedenti() {
      this.showModalOrdiniPrecedenti = true;
    },
    apriChiusuraRapida(ordine) {
      this.ordineInChiusura = ordine;
      this.metodoPagamentoChiusura = 'contanti';
      this.scontoChiusura = 0;
    },
    confermaChiusuraOrdine() {
      if (!this.ordineInChiusura) return;
      const idT = this.ordineInChiusura.idT || this.ordineInChiusura.codiceTavolo;
      const idO = this.ordineInChiusura.idO || this.ordineInChiusura.idOrdine || this.ordineInChiusura.codicemenu || this.ordineInChiusura._id;
      const tot = this.calcolaTotaleOrdine(this.ordineInChiusura);
      const isDigitale = this.metodoPagamentoChiusura === 'digitale';
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

      this.axios.post(this.store.path + 'chiudiOrdine', {
        idT: idT,
        idO: idO,
        totale: tot,
        sconto: this.scontoChiusura || 0,
        digitale: isDigitale,
        headers: {
          authorization: `Bearer ${storedToken}`
        }
      }).then(response => {
        if (response.data) {
          alert("Tavolo chiuso correttamente!");
          this.ordineInChiusura = null;
          this.fetchOrdiniApertiPrecedenti();
          this.displaySala();
        } else {
          alert("Impossibile chiudere l'ordine.");
        }
      }).catch(err => {
        console.error("Errore chiusura ordine:", err);
        alert("Errore durante la chiusura del tavolo.");
      });
    },
    eliminaPiattoPrecedente(ordine, piatto) {
      const piattoNome = piatto.piatto || piatto.nome || 'questo piatto';
      if (!confirm(`Sei sicuro di voler eliminare ${piattoNome} dall'ordine?`)) return;

      const passwordAdmin = prompt("Inserisci la password amministratore (se richiesta):") || '';
      const idO = ordine.idO || ordine.idOrdine || ordine.codicemenu || ordine._id;
      const piattoId = piatto.piattoId || piatto._id;
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

      this.axios.post(this.store.path + 'rimuoviPiattoDaOrdine', {
        idOrdine: idO,
        piattoId: piattoId,
        passwordAdmin: passwordAdmin
      }, {
        headers: { authorization: `Bearer ${storedToken}` }
      }).then(response => {
        if (response.data) {
          alert("Piatto rimosso dall'ordine.");
          this.fetchOrdiniApertiPrecedenti();
        }
      }).catch(err => {
        console.error("Errore rimozione piatto:", err);
        const msg = err.response?.data?.errore || "Errore durante la rimozione del piatto.";
        alert(msg);
      });
    },
    eliminaTavoloPrecedente(ordine) {
      if (!confirm("Sei sicuro di voler eliminare l'intero tavolo/ordine? L'azione non è reversibile.")) return;

      const passwordAdmin = prompt("Inserisci la password amministratore per confermare:") || '';
      const idO = ordine.idO || ordine.idOrdine || ordine.codicemenu || ordine._id;
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

      this.axios.post(this.store.path + 'eliminaOrdine', {
        idOrdine: idO,
        passwordAdmin: passwordAdmin
      }, {
        headers: { authorization: `Bearer ${storedToken}` }
      }).then(response => {
        if (response.data) {
          alert("Tavolo/Ordine eliminato con successo.");
          this.fetchOrdiniApertiPrecedenti();
          this.displaySala();
        }
      }).catch(err => {
        console.error("Errore eliminazione tavolo:", err);
        const msg = err.response?.data?.errore || "Errore durante l'eliminazione del tavolo.";
        alert(msg);
      });
    },
    goToSalaTavolo(salaNome) {
      this.showModalOrdiniPrecedenti = false;
      this.goToSala(salaNome, null);
    },
    fetchOrdiniApertiPrecedenti() {
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';
      const startOfToday = moment().startOf('day').unix();

      // First attempt dedicated endpoint, with fallback to displayOrdini with soloPrecedenti
      this.axios.post(this.store.path + 'getOrdiniApertiPrecedenti', {}, {
        headers: { authorization: `Bearer ${storedToken}` }
      }).then(response => {
        if (response.data && response.data.messaggio) {
          this.ordiniPrecedenti = response.data.messaggio;
        }
      }).catch(() => {
        // Fallback to displayOrdini
        this.axios.post(this.store.path + 'displayOrdini', {
          soloPrecedenti: true,
          stato: 'ordinato',
          end: startOfToday
        }, {
          headers: { authorization: `Bearer ${storedToken}` }
        }).then(res => {
          if (res.data && res.data.messaggio) {
            this.ordiniPrecedenti = res.data.messaggio;
          }
        }).catch(err => {
          console.warn("Impossibile recuperare ordini precedenti:", err);
        });
      });
    },
    getSala() {
      if (!this.nome || !this.numero) return;
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

      this.axios.post(this.store.path + 'createSala', {
        nome: this.nome,
        numero: this.numero,
        headers: {
          authorization: `Bearer ${storedToken}`
        }
      }).then((response) => {
        if (response.data) {
          this.displaySala();
          this.page = '';
          this.nome = '';
          this.numero = '';
        }
      }).catch(err => {
        console.error("Errore creazione sala:", err);
      });
    },
    displaySala() {
      const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

      this.axios.post(this.store.path + 'displaySala', {
        headers: {
          authorization: `Bearer ${storedToken}`
        }
      }).then((response) => {
        const rv = [];
        const rvnome = [];
        const rvnumero = [];
        if (response.data && response.data.messaggio) {
          response.data.messaggio.forEach(element => {
            rv.push(element);
            rvnome.push(element.nome);
            rvnumero.push(element.numero);
          });
          this.sale = rv;
          this.nomeSale = rvnome;
          this.numeroSale = rvnumero;
        }
      }).catch(err => {
        console.error("Errore recupero sale:", err);
      });
    },
    goToSala(sala, numero) {
      this.$router.push({
        name: "VisualizzaSala",
        params: {
          nome: sala,
          numero: numero || 50,
          sale: this.nomeSale,
          numeroSale: this.numeroSale
        }
      });
    },
    deleteSala(sala) {
      if (confirm(`${this.$t('elimina_sala')} ${sala}?`)) {
        const passwordAdmin = prompt("Inserisci la password amministratore per eliminare la sala:");
        if (passwordAdmin === null) return;

        const storedToken = localStorage.token ? JSON.parse(localStorage.token) : '';

        this.axios.post(this.store.path + 'deleteSala', {
          sala: sala,
          passwordAdmin: passwordAdmin,
          headers: {
            authorization: `Bearer ${storedToken}`
          }
        }).then((response) => {
          if (response.data && response.data.messaggio) {
            this.displaySala();
          }
        }).catch((error) => {
          console.error("Errore durante l'eliminazione:", error);
          const msg = error.response?.data?.messaggio || "Errore durante l'eliminazione della sala.";
          alert(msg);
        });
      }
    }
  },
  beforeMount() {
    this.displaySala();
    this.fetchOrdiniApertiPrecedenti();
  },
}
</script>

<style scoped>
.sala-main-container {
  max-width: 1380px;
  margin: 0 auto;
  padding: 24px 20px 48px;
  box-sizing: border-box;
}

/* Von Restorff High Saliency Alert Banner */
.ordini-precedenti-banner {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 20px;
  background: var(--sci-amber-subtle, #fffbeb);
  border: 1px solid var(--sci-amber-border, #fde68a);
  border-radius: var(--radius-lg, 16px);
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.12);
}

.banner-pulse-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  background: #fef3c7;
  color: var(--sci-amber, #d97706);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  animation: pulse-ring 2s infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(217, 119, 6, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
}

.banner-info-col {
  flex: 1;
  text-align: left;
}

.banner-title {
  font-size: 16px;
  font-weight: 700;
  color: #92400e;
  margin: 0 0 2px;
}

.banner-subtitle {
  font-size: 13.5px;
  color: #78350f;
  margin: 0;
}

.banner-action-col {
  white-space: nowrap;
}

/* Top Action Bar */
.sala-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.header-title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--sci-text-main, #0f172a);
  margin: 0 0 4px;
}

.header-subtitle {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Creation Panel */
.crea-panel {
  background: #ffffff;
  border-radius: var(--radius-lg, 16px);
  padding: 24px;
  border: 1px solid var(--sci-border, #e2e8f0);
}

.panel-header {
  margin-bottom: 20px;
}

.panel-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px;
}

.panel-desc {
  font-size: 13px;
  color: #64748b;
}

.form-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.form-col {
  flex: 1;
  min-width: 240px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  margin-bottom: 6px;
}

.input-with-icon {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 14px;
}

.input-with-icon .sci-input {
  padding-left: 40px;
}

.panel-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Rooms Grid */
.rooms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.room-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px;
  border-radius: var(--radius-lg, 16px);
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.04);
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.room-card.room-has-precedenti {
  border-color: #fde68a;
  background: #fffdfa;
}

.room-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 24px -4px rgba(15, 23, 42, 0.08);
  border-color: #cbd5e1;
}

.room-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.room-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--sci-primary-subtle, #eef2ff);
  color: var(--sci-primary, #4f46e5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.room-delete-btn {
  border: none;
  background: transparent;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.room-delete-btn:hover {
  background: var(--sci-rose-subtle, #fff1f2);
  color: var(--sci-rose, #e11d48);
}

.room-card-body {
  margin-bottom: 24px;
}

.room-name {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sci-text-main, #0f172a);
  margin: 0 0 10px;
}

.room-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.room-card-footer {
  margin-top: auto;
}

.enter-room-btn {
  width: 100%;
  justify-content: space-between;
  min-height: 46px;
}

/* Empty State */
.empty-rooms {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-icon {
  font-size: 44px;
  color: #94a3b8;
}

/* Modal Styling */
.sci-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.sci-modal-card {
  background: #ffffff;
  border-radius: var(--radius-xl, 20px);
  max-width: 820px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  border: 1px solid var(--sci-border, #e2e8f0);
  overflow: hidden;
  animation: modalSlideUp 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes modalSlideUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.sci-modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--sci-bg-subtle, #f8fafc);
}

.modal-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close-modal {
  border: none;
  background: transparent;
  color: #94a3b8;
  font-size: 18px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.sci-modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.ordini-precedenti-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ordine-precedente-item {
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: var(--radius-md, 14px);
  padding: 16px 20px;
  background: #ffffff;
}

.item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--sci-border, #f1f5f9);
  margin-bottom: 12px;
}

.item-title-box {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.badge-tavolo {
  background: #0f172a;
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
}

.badge-sala {
  background: #eef2ff;
  color: #4f46e5;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12.5px;
}

.badge-date {
  background: #fffbeb;
  color: #92400e;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
}

.item-meta-right {
  display: flex;
  gap: 10px;
  font-size: 12px;
  color: #64748b;
}

.item-dishes-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.dish-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--sci-bg-subtle, #f8fafc);
  border-radius: 8px;
}

.dish-qty-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dish-qty {
  font-weight: 700;
  color: #4f46e5;
  font-size: 13px;
}

.dish-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 13.5px;
}

.dish-additions {
  font-size: 12px;
  color: #059669;
}

.dish-subtractions {
  font-size: 12px;
  color: #e11d48;
}

.dish-note {
  font-size: 11.5px;
  color: #64748b;
  font-style: italic;
}

.dish-price-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dish-price {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.btn-delete-dish {
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-delete-dish:hover {
  background: #fee2e2;
  color: #e11d48;
}

.item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--sci-border, #f1f5f9);
}

.item-totale {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.label-totale {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}

.valore-totale {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.item-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* Payment Chip Grid */
.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.payment-method-chip {
  padding: 10px 14px;
  border: 1px solid var(--sci-border, #cbd5e1);
  border-radius: 10px;
  text-align: center;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s ease;
}

.payment-method-chip.active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #ffffff;
}

/* Transitions */
.expand-enter-active, .expand-leave-active {
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  max-height: 400px;
  overflow: hidden;
}

.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}
</style>
